import React, { useState } from 'react';
import { formatDateTime, getStatusColor, getPriorityColor, getUserNameByEmail, formatDisplayNumber } from './utils/helpers';
import './FeatureRequestBoard.css';

const STATUSES = ['Submitted', 'Under Review', 'Planned', 'In Progress', 'Testing', 'Completed', 'Rejected'];

function FeatureRequestBoard({
  requests,
  user,
  userRole,
  users,
  onEditRequest,
  onViewRequest,
  onVote,
  onAddComment,
  commentInputs,
  setCommentInputs,
  editingComment,
  editCommentText,
  setEditCommentText,
  onEditComment,
  onSaveCommentEdit,
  onCancelCommentEdit,
  onConvertToDefect,
  onHiddenLanesChange
}) {
  const [hiddenLanes, setHiddenLanes] = useState(new Set());
  const [expandedRequests, setExpandedRequests] = useState(new Set());
  const [laneSorts, setLaneSorts] = useState({}); // { status: { field: 'priority', direction: 'desc' } }

  const isAdmin = userRole === 'Admin';
  const isTester = userRole === 'Tester/QA';

  const toggleLane = (status) => {
    setHiddenLanes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(status)) {
        newSet.delete(status);
      } else {
        newSet.add(status);
      }
      // Notify parent component of hidden lanes change
      if (onHiddenLanesChange) {
        onHiddenLanesChange(newSet);
      }
      return newSet;
    });
  };

  const handleSort = (status, field) => {
    setLaneSorts(prev => {
      const current = prev[status];
      let newDirection = 'desc';
      
      if (current && current.field === field) {
        newDirection = current.direction === 'desc' ? 'asc' : 'desc';
      }
      
      return {
        ...prev,
        [status]: { field, direction: newDirection }
      };
    });
  };

  const sortRequests = (requestsToSort, status) => {
    const sortConfig = laneSorts[status] || { field: 'votes', direction: 'desc' };
    const PRIORITY_ORDER = { 'Critical': 4, 'High': 3, 'Medium': 2, 'Low': 1 };
    
    return [...requestsToSort].sort((a, b) => {
      let aVal, bVal;
      
      if (sortConfig.field === 'priority') {
        aVal = PRIORITY_ORDER[a.priority] || 0;
        bVal = PRIORITY_ORDER[b.priority] || 0;
      } else if (sortConfig.field === 'votes') {
        aVal = (a.upvotes?.length || 0) - (a.downvotes?.length || 0);
        bVal = (b.upvotes?.length || 0) - (b.downvotes?.length || 0);
      } else if (sortConfig.field === 'createdAt') {
        aVal = a.createdAt?.toMillis() || 0;
        bVal = b.createdAt?.toMillis() || 0;
      } else if (sortConfig.field === 'updatedAt') {
        aVal = a.updatedAt?.toMillis() || 0;
        bVal = b.updatedAt?.toMillis() || 0;
      } else if (sortConfig.field === 'applicationName') {
        aVal = (a.applicationName || '').toLowerCase();
        bVal = (b.applicationName || '').toLowerCase();
        // String comparison
        if (sortConfig.direction === 'desc') {
          return bVal.localeCompare(aVal);
        } else {
          return aVal.localeCompare(bVal);
        }
      }
      
      if (sortConfig.direction === 'desc') {
        return bVal - aVal;
      } else {
        return aVal - bVal;
      }
    });
  };

  const toggleExpanded = (requestId) => {
    setExpandedRequests(prev => {
      const newSet = new Set(prev);
      if (newSet.has(requestId)) {
        newSet.delete(requestId);
      } else {
        newSet.add(requestId);
      }
      return newSet;
    });
  };

  const getUserVote = (request) => {
    if (!request.votes || !user) return null;
    return request.votes[user.uid] || null;
  };

  const getSortIcon = (status, field) => {
    const sortConfig = laneSorts[status];
    if (!sortConfig || sortConfig.field !== field) return '⇅';
    return sortConfig.direction === 'desc' ? '▼' : '▲';
  };

  return (
    <>
      {/* Lane Visibility Controls */}
      <div className="lane-visibility-controls">
        <span className="controls-label">Show/Hide Lanes:</span>
        {STATUSES.map(status => (
          <button
            key={status}
            className={`lane-visibility-btn ${hiddenLanes.has(status) ? 'lane-visibility-btn--hidden' : ''}`}
            onClick={() => toggleLane(status)}
            style={{ backgroundColor: getStatusColor(status) }}
          >
            {hiddenLanes.has(status) ? '👁️' : '👁️‍🗨️'} {status}
          </button>
        ))}
      </div>

      <div className="feature-board-wrapper">
        <div className="feature-board">
        {STATUSES.map(status => {
          const statusRequests = requests.filter(r => r.status === status);
          const laneRequests = sortRequests(statusRequests, status);
          const isHidden = hiddenLanes.has(status);

          // Skip rendering if hidden
          if (isHidden) return null;

          return (
            <div key={status} className="feature-lane">
              <div className="feature-lane-header" style={{ backgroundColor: getStatusColor(status) }}>
                <div className="feature-lane-title">
                  <span>{status}</span>
                  <span className="feature-lane-count">{laneRequests.length}</span>
                </div>
              </div>
              
              {/* Sort Controls */}
              <div className="swimlane-sort-controls">
                <button 
                  className="sort-btn" 
                  onClick={() => handleSort(status, 'votes')}
                  title="Sort by Votes"
                >
                  Votes {getSortIcon(status, 'votes')}
                </button>
                <button 
                  className="sort-btn" 
                  onClick={() => handleSort(status, 'priority')}
                  title="Sort by Priority"
                >
                  Priority {getSortIcon(status, 'priority')}
                </button>
                <button 
                  className="sort-btn" 
                  onClick={() => handleSort(status, 'createdAt')}
                  title="Sort by Created"
                >
                  Created {getSortIcon(status, 'createdAt')}
                </button>
                <button 
                  className="sort-btn" 
                  onClick={() => handleSort(status, 'updatedAt')}
                  title="Sort by Updated"
                >
                  Updated {getSortIcon(status, 'updatedAt')}
                </button>
                <button 
                  className="sort-btn" 
                  onClick={() => handleSort(status, 'applicationName')}
                  title="Sort by Application"
                >
                  Application {getSortIcon(status, 'applicationName')}
                </button>
              </div>

              <div className="feature-lane-content">
                {laneRequests.length === 0 ? (
                  <div className="empty-lane">No requests</div>
                ) : (
                  laneRequests.map(request => {
                    const isExpanded = expandedRequests.has(request.id);
                    const userVote = getUserVote(request);

                    return (
                      <div key={request.id} className="feature-card">
                        <div className="feature-card-header">
                          <div className="feature-card-title-wrap">
                            {typeof request.displayNumber === 'number' && (
                              <span className="feature-card-seq" title="Sequential number for this application">
                                {formatDisplayNumber(request, 'F')}
                              </span>
                            )}
                            <h4 className="feature-card-title">{request.title}</h4>
                          </div>
                          <div className="feature-card-votes">
                            <button
                              className={`vote-btn vote-btn--up ${userVote === 'up' ? 'vote-btn--active' : ''}`}
                              onClick={() => onVote(request, 'up')}
                              title="Upvote"
                            >
                              👍
                            </button>
                            <span className="vote-count">{request.voteCount || 0}</span>
                            <button
                              className={`vote-btn vote-btn--down ${userVote === 'down' ? 'vote-btn--active' : ''}`}
                              onClick={() => onVote(request, 'down')}
                              title="Downvote"
                            >
                              👎
                            </button>
                          </div>
                        </div>

                        <div className="feature-card-meta">
                          {request.applicationName && (
                            <span className="app-badge">
                              📱 {request.applicationName}
                            </span>
                          )}
                          <span 
                            className="priority-badge"
                            style={{ backgroundColor: getPriorityColor(request.priority) }}
                          >
                            {request.priority}
                          </span>
                          <span className="requester">
                            By: {getUserNameByEmail(request.createdByEmail)}
                          </span>
                        </div>

                        {(request.assignedToDev || request.assignedToTest) && (
                          <div className="feature-card-assignments">
                            {request.assignedToDev && (
                              <span className="assignment-badge assignment-badge--dev">
                                👷 Dev: {getUserNameByEmail(request.assignedToDev)}
                              </span>
                            )}
                            {request.assignedToTest && (
                              <span className="assignment-badge assignment-badge--test">
                                🧪 Test: {getUserNameByEmail(request.assignedToTest)}
                              </span>
                            )}
                          </div>
                        )}

                        <p className="feature-card-description">
                          {request.description}
                        </p>

                        {request.testStep && (
                          <div className="feature-test-step">
                            <strong>Test Step:</strong> {request.testStep}
                          </div>
                        )}

                        {request.mockupImageUrl && (
                          <div className="feature-mockup">
                            <img 
                              src={request.mockupImageUrl} 
                              alt="Feature mockup"
                              onClick={() => window.open(request.mockupImageUrl, '_blank')}
                            />
                          </div>
                        )}

                        <div className="feature-card-footer">
                          <div className="feature-card-dates">
                            <strong>Created:</strong> {formatDateTime(request.createdAt)}
                          </div>
                          {request.updatedAt && (
                            <div className="feature-card-dates">
                              <strong>Updated:</strong> {formatDateTime(request.updatedAt)}
                            </div>
                          )}
                        </div>

                        <div className="feature-card-actions">
                          <button 
                            className="btn btn-small btn-view" 
                            onClick={() => onViewRequest(request)}
                          >
                            👁️ View
                          </button>
                          {(isAdmin || isTester) && (
                            <>
                              <button 
                                className="btn btn-small btn-secondary" 
                                onClick={() => onEditRequest(request)}
                              >
                                Edit
                              </button>
                              <button 
                                className="btn btn-small btn-secondary" 
                                onClick={() => onConvertToDefect(request)}
                                title="Convert this feature request to a defect"
                              >
                                🔄 To Defect
                              </button>
                            </>
                          )}
                          <button
                            className="btn btn-small btn-secondary"
                            onClick={() => toggleExpanded(request.id)}
                          >
                            {isExpanded ? 'Hide' : 'Show'} Comments ({request.comments?.length || 0})
                          </button>
                        </div>

                        {isExpanded && (
                          <div className="feature-comments-section">
                            <div className="comments-list">
                              {request.comments?.length === 0 ? (
                                <p className="no-comments">No comments yet</p>
                              ) : (
                                request.comments.map((comment, idx) => {
                                  const isEditing = editingComment === `${request.id}-${idx}`;
                                  const canEditComment = user.email === comment.createdBy || isAdmin || isTester;
                                  
                                  return (
                                    <div key={idx} className="comment">
                                      <div className="comment-header">
                                        <strong>{getUserNameByEmail(comment.createdBy)}</strong>
                                        <span className="comment-date">
                                          {formatDateTime(comment.createdAt)}
                                          {comment.editedAt && ' (edited)'}
                                        </span>
                                      </div>
                                      
                                      {isEditing ? (
                                        <div className="comment-edit-mode">
                                          <textarea
                                            value={editCommentText}
                                            onChange={e => setEditCommentText(e.target.value)}
                                            className="comment-edit-input"
                                          />
                                          <div className="comment-edit-actions">
                                            <button 
                                              className="btn btn-small btn-primary"
                                              onClick={() => onSaveCommentEdit(request.id, idx)}
                                            >
                                              Save
                                            </button>
                                            <button 
                                              className="btn btn-small btn-secondary"
                                              onClick={onCancelCommentEdit}
                                            >
                                              Cancel
                                            </button>
                                          </div>
                                        </div>
                                      ) : (
                                        <>
                                          <p className="comment-text">{comment.text}</p>
                                          {canEditComment && (
                                            <div className="comment-actions">
                                              <button
                                                className="btn-link btn-link--small"
                                                onClick={() => onEditComment(request.id, idx, comment)}
                                              >
                                                Edit
                                              </button>
                                            </div>
                                          )}
                                        </>
                                      )}
                                    </div>
                                  );
                                })
                              )}
                            </div>

                            <div className="comment-input-container">
                              <input
                                type="text"
                                className="comment-input"
                                placeholder="Add a comment..."
                                value={commentInputs[request.id] || ''}
                                onChange={e => setCommentInputs(prev => ({
                                  ...prev,
                                  [request.id]: e.target.value
                                }))}
                                onKeyPress={e => {
                                  if (e.key === 'Enter') {
                                    onAddComment(request, commentInputs[request.id]);
                                  }
                                }}
                              />
                              <button
                                className="btn btn-small btn-primary"
                                onClick={() => onAddComment(request, commentInputs[request.id])}
                              >
                                Add
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          );
        })}
        </div>
      </div>
    </>
  );
}

export default FeatureRequestBoard;
